import { FEATURED_WORK } from "#/lib/constants";
import { img } from "#/lib/images";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { CTAButton } from "#/components/shared/CTAButton";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";
import { MapPin } from "lucide-react";

/**
 * Section 7 — concise recent-work teaser. Three projects, uniform cards, all
 * roads lead to /our-work (the full grid + Delaware map live there).
 */
export function FeaturedWork() {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <section id="work" className="bg-light-bg py-20 lg:py-28">
      <div ref={ref} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header. The "See All Our Work" button used to sit up here beside the
            heading, which put the exit link before the work it is meant to
            follow. It now lives under the three cards. */}
        <div className="max-w-2xl">
          <SectionLabel>Recent Work</SectionLabel>
          <h2
            className={`mt-4 font-heading text-3xl font-extrabold leading-[1.1] text-graphite transition-all duration-700 sm:text-4xl lg:text-5xl ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            Concrete we're proud to put our name on.
          </h2>
        </div>

        {/* Teaser row — first three projects, uniform cards */}
        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {FEATURED_WORK.slice(0, 3).map((project, i) => (
            <a
              key={project.title}
              href="/our-work"
              className={`group relative block overflow-hidden rounded-2xl transition-all duration-500 ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: isVisible ? `${i * 80}ms` : "0ms" }}
            >
              <img
                src={img(project.img, { w: 600, h: 460 })}
                alt={`${project.title}, ${project.location}`}
                width={600}
                height={460}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] h-auto w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-graphite/85 via-graphite/10 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-graphite backdrop-blur">
                {project.tag}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-heading text-base font-bold text-white">
                  {project.title}
                </h3>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-concrete-light">
                  <MapPin className="h-3.5 w-3.5 text-flame-light" />
                  {project.location}
                </p>
              </div>
            </a>
          ))}
        </div>

        {/* Exit link, after the work it follows. Full width on mobile so it
            reads as the section's closing action rather than a stray chip. */}
        <div className="mt-10 flex justify-center">
          <CTAButton
            href="/our-work"
            variant="dark-outline"
            className="w-full sm:w-auto"
          >
            See All Our Work
          </CTAButton>
        </div>
      </div>
    </section>
  );
}
